import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

// Indicador de força de senha
function PasswordStrength({ password }) {
  const checks = [
    { label: 'Mínimo 6 caracteres',       ok: password.length >= 6 },
    { label: 'Letra maiúscula',            ok: /[A-Z]/.test(password) },
    { label: 'Letra minúscula',            ok: /[a-z]/.test(password) },
    { label: 'Número',                     ok: /[0-9]/.test(password) },
    { label: 'Caractere especial (@#!...)',ok: /[^A-Za-z0-9]/.test(password) },
  ];
  const score = checks.filter(c => c.ok).length;
  const colors = ['#e5e5e5','#e74c3c','#e67e22','#f1c40f','#2ecc71','#27ae60'];
  const labels = ['','Muito fraca','Fraca','Regular','Boa','Forte'];

  if (!password) return null;

  return (
    <div style={{ marginTop:8 }}>
      <div style={{ display:'flex', gap:4, marginBottom:6 }}>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{
            flex:1, height:4, borderRadius:2,
            background: i <= score ? colors[score] : '#e5e5e5',
            transition:'background .3s'
          }} />
        ))}
      </div>
      <p style={{ fontSize:11, color: colors[score], fontWeight:700, margin:'0 0 6px' }}>
        {labels[score]}
      </p>
      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
        {checks.map(c => (
          <span key={c.label} style={{ fontSize:11, color: c.ok ? '#2ecc71' : '#bbb', display:'flex', gap:6, alignItems:'center' }}>
            <span>{c.ok ? '✓' : '○'}</span> {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate  = useNavigate();

  const [status, setStatus]     = useState('loading'); // loading | valid | invalid | success
  const [userData, setUserData] = useState(null);
  const [tokenMsg, setTokenMsg] = useState('');

  const [form, setForm]       = useState({ password: '', confirmPassword: '' });
  const [showPwd, setShowPwd] = useState({ p1: false, p2: false });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  // Valida token ao montar
  useEffect(() => {
    axios.get(`${API}/api/auth/reset-password/${token}`)
      .then(({ data }) => {
        setUserData(data);
        setStatus('valid');
      })
      .catch(err => {
        setTokenMsg(err.response?.data?.error || 'Link inválido ou expirado');
        setStatus('invalid');
      });
  }, [token]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword)
      return setError('As senhas não coincidem');
    if (form.password.length < 6)
      return setError('A senha deve ter no mínimo 6 caracteres');

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password/${token}`, form);
      setStatus('success');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao redefinir senha');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth:440 }}>

        {/* Logo */}
        <div className="auth-logo">
          <img src="/favicon.ico" alt="Logo" style={{ width:52, height:52, objectFit:'contain', marginBottom:4 }} />
          <h1 className="auth-title">POLITICS SYSTEM</h1>
          <p className="auth-sub">Redefinição de Senha</p>
        </div>

        {/* LOADING */}
        {status === 'loading' && (
          <div style={{ textAlign:'center', padding:'20px 0', color:'#777' }}>
            <div className="spinner" style={{ margin:'0 auto 12px' }} />
            Validando link...
          </div>
        )}

        {/* INVÁLIDO / EXPIRADO */}
        {status === 'invalid' && (
          <div style={{ display:'flex', flexDirection:'column', gap:20, textAlign:'center' }}>
            <div style={{ fontSize:48 }}>⛔</div>
            <h3 style={{ fontSize:17, fontWeight:800, color:'#c0392b', margin:0 }}>Link inválido</h3>
            <p style={{ fontSize:14, color:'#555', lineHeight:1.7, margin:0 }}>{tokenMsg}</p>
            <Link to="/recuperar-senha" className="btn"
              style={{ padding:'13px', fontSize:14, letterSpacing:1, textDecoration:'none', display:'block', textAlign:'center' }}>
              SOLICITAR NOVO LINK
            </Link>
            <Link to="/login" style={{ fontSize:13, color:'#000080', textDecoration:'none', fontWeight:600 }}>
              ← Voltar para o login
            </Link>
          </div>
        )}

        {/* FORMULÁRIO */}
        {status === 'valid' && (
          <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {userData && (
              <div style={{ background:'#f0fff4', border:'1px solid #a8e6c0', borderRadius:8,
                padding:'12px 16px', fontSize:13, color:'#1a7a40', lineHeight:1.6 }}>
                <strong>👤 {userData.nome}</strong> — login: <strong>{userData.login}</strong><br />
                Crie uma nova senha para acessar o sistema.
              </div>
            )}

            {/* Nova senha */}
            <div className="field-group">
              <label className="r-label">NOVA SENHA</label>
              <div style={{ position:'relative' }}>
                <input
                  className="r-input"
                  type={showPwd.p1 ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="Mínimo 6 caracteres"
                  required
                  style={{ paddingRight:40 }}
                />
                <button type="button"
                  onClick={() => setShowPwd(s => ({...s, p1: !s.p1}))}
                  style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', cursor:'pointer', fontSize:16, color:'#888' }}>
                  {showPwd.p1 ? '🙈' : '👁'}
                </button>
              </div>
              <PasswordStrength password={form.password} />
            </div>

            {/* Confirmar senha */}
            <div className="field-group">
              <label className="r-label">CONFIRMAR NOVA SENHA</label>
              <div style={{ position:'relative' }}>
                <input
                  className="r-input"
                  type={showPwd.p2 ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={e => setForm({...form, confirmPassword: e.target.value})}
                  placeholder="Repita a nova senha"
                  required
                  style={{ paddingRight:40,
                    borderColor: form.confirmPassword
                      ? form.password === form.confirmPassword ? '#2ecc71' : '#e74c3c'
                      : undefined
                  }}
                />
                <button type="button"
                  onClick={() => setShowPwd(s => ({...s, p2: !s.p2}))}
                  style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', cursor:'pointer', fontSize:16, color:'#888' }}>
                  {showPwd.p2 ? '🙈' : '👁'}
                </button>
              </div>
              {form.confirmPassword && (
                <p style={{ fontSize:11, marginTop:4,
                  color: form.password === form.confirmPassword ? '#2ecc71' : '#e74c3c' }}>
                  {form.password === form.confirmPassword ? '✓ Senhas coincidem' : '✗ Senhas não coincidem'}
                </p>
              )}
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <button className="btn" disabled={loading}
              style={{ padding:'13px', fontSize:14, letterSpacing:1, marginTop:4 }}>
              {loading ? 'SALVANDO...' : 'SALVAR NOVA SENHA'}
            </button>
          </form>
        )}

        {/* SUCESSO */}
        {status === 'success' && (
          <div style={{ display:'flex', flexDirection:'column', gap:20, textAlign:'center' }}>
            <div style={{ fontSize:56, lineHeight:1 }}>🎉</div>
            <h3 style={{ fontSize:18, fontWeight:800, color:'#111', margin:0 }}>
              Senha redefinida!
            </h3>
            <p style={{ fontSize:14, color:'#555', lineHeight:1.7, margin:0 }}>
              Sua senha foi alterada com sucesso. Use a nova senha para acessar o sistema.
            </p>
            <button className="btn" onClick={() => navigate('/login')}
              style={{ padding:'13px', fontSize:14, letterSpacing:1 }}>
              IR PARA O LOGIN
            </button>
          </div>
        )}

        {/* Rodapé */}
        <div style={{ marginTop:24, borderTop:'1px solid #e0e0e0', paddingTop:14, textAlign:'center' }}>
          <p style={{ fontSize:11, color:'#aaa', margin:0 }}>Todos os direitos reservados PoliticSYS©</p>
        </div>
      </div>
    </div>
  );
}
